/**
 * Customer Portal (UI) - templates route wired to live Pages APIs.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { deleteTemplate, fetchTemplates, upsertTemplate } from '../features/api/client';
import type { TemplateItem } from '../types/portal';
import { formatPortalDateTime, type TimeDisplayMode } from '../utils/datetime';

type DraftTemplate = {
  template_key: string;
  body: string;
};

type TemplateStatusFilter = 'all' | TemplateItem['status'];

type TemplateFilters = {
  templateKey: string;
  status: TemplateStatusFilter;
  bodyContains: string;
};

type RequiredVarSelection = Record<string, boolean>;

type EncodingName = 'GSM-7' | 'Unicode (UCS-2)';

type NonGsmCharacterMatch = {
  character: string;
  index: number;
  codePoint: string;
  suggested: string | null;
};

type BodyPreviewChunk = {
  text: string;
  isNonGsm: boolean;
};

const GSM7_BASIC = new Set([
  '@',
  '£',
  '$',
  '¥',
  'è',
  'é',
  'ù',
  'ì',
  'ò',
  'Ç',
  '\n',
  'Ø',
  'ø',
  '\r',
  'Å',
  'å',
  'Δ',
  '_',
  'Φ',
  'Γ',
  'Λ',
  'Ω',
  'Π',
  'Ψ',
  'Σ',
  'Θ',
  'Ξ',
  'Æ',
  'æ',
  'ß',
  'É',
  ' ',
  '!',
  '"',
  '#',
  '¤',
  '%',
  '&',
  "'",
  '(',
  ')',
  '*',
  '+',
  ',',
  '-',
  '.',
  '/',
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  ':',
  ';',
  '<',
  '=',
  '>',
  '?',
  '¡',
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
  'Ä',
  'Ö',
  'Ñ',
  'Ü',
  '§',
  '¿',
  'a',
  'b',
  'c',
  'd',
  'e',
  'f',
  'g',
  'h',
  'i',
  'j',
  'k',
  'l',
  'm',
  'n',
  'o',
  'p',
  'q',
  'r',
  's',
  't',
  'u',
  'v',
  'w',
  'x',
  'y',
  'z',
  'ä',
  'ö',
  'ñ',
  'ü',
  'à'
]);

const GSM7_EXT = new Set(['^', '{', '}', '\\', '[', '~', ']', '|', '€']);

const SMART_REPLACEMENTS: Array<[string, string]> = [
  ['\u201C', '"'],
  ['\u201D', '"'],
  ['\u2018', "'"],
  ['\u2019', "'"],
  ['\u201B', "'"],
  ['\u2014', '-'],
  ['\u2013', '-'],
  ['\u2012', '-'],
  ['\u2011', '-'],
  ['\u2010', '-'],
  ['\u2026', '...'],
  ['\u00A0', ' '],
  ['\u2022', '*'],
  ['\u00B7', '.'],
  ['\u200B', ''],
  ['\u200C', ''],
  ['\u200D', ''],
  ['\uFEFF', '']
];

const SMART_REPLACEMENT_MAP = new Map<string, string>(SMART_REPLACEMENTS);

function isGsm7Character(character: string): boolean {
  return GSM7_BASIC.has(character) || GSM7_EXT.has(character);
}

function formatCodePoint(character: string): string {
  const value = character.codePointAt(0);
  return value === undefined ? 'U+0000' : `U+${value.toString(16).toUpperCase().padStart(4, '0')}`;
}

function getTemplateEncoding(body: string): EncodingName {
  for (const character of body) {
    if (!isGsm7Character(character)) {
      return 'Unicode (UCS-2)';
    }
  }
  return 'GSM-7';
}

function getGsm7SeptetLength(body: string): number {
  let septets = 0;
  for (const character of body) {
    if (GSM7_BASIC.has(character)) {
      septets += 1;
      continue;
    }
    if (GSM7_EXT.has(character)) {
      septets += 2;
      continue;
    }
    // Fallback should not be hit for GSM-7, but keeps behavior safe.
    septets += 2;
  }
  return septets;
}

function estimateSmsSegments(body: string, encodingName: EncodingName): number {
  if (!body.length) {
    return 1;
  }

  if (encodingName === 'GSM-7') {
    const septets = getGsm7SeptetLength(body);
    const perSegment = septets <= 160 ? 160 : 153;
    return Math.ceil(septets / perSegment);
  }

  // UCS-2: 70 chars single-segment, 67 chars per segment when concatenated.
  const perSegment = body.length <= 70 ? 70 : 67;
  return Math.ceil(body.length / perSegment);
}

function getNonGsmCharacterMatches(body: string): NonGsmCharacterMatch[] {
  const matches: NonGsmCharacterMatch[] = [];
  let index = 0;
  for (const character of body) {
    if (!isGsm7Character(character)) {
      matches.push({
        character,
        index,
        codePoint: formatCodePoint(character),
        suggested: SMART_REPLACEMENT_MAP.get(character) ?? null
      });
    }
    index += character.length;
  }
  return matches;
}

function buildBodyPreviewChunks(body: string): BodyPreviewChunk[] {
  const chunks: BodyPreviewChunk[] = [];
  let activeText = '';
  let activeType: boolean | null = null;
  for (const character of body) {
    const isNonGsm = !isGsm7Character(character);
    if (activeType === null || activeType === isNonGsm) {
      activeText += character;
      activeType = isNonGsm;
      continue;
    }
    chunks.push({ text: activeText, isNonGsm: activeType });
    activeText = character;
    activeType = isNonGsm;
  }
  if (activeText) {
    chunks.push({ text: activeText, isNonGsm: Boolean(activeType) });
  }
  return chunks;
}

function applySmartReplacements(body: string): string {
  if (!body) return body;
  let transformed = '';
  for (const character of body) {
    transformed += SMART_REPLACEMENT_MAP.get(character) ?? character;
  }
  return transformed;
}

function renderTemplateStatusBadge(status: TemplateItem['status'] | undefined) {
  const normalized = status ?? 'IN_REVIEW';
  const styleByStatus: Record<TemplateItem['status'], string> = {
    IN_REVIEW: 'bg-yellow-lt text-yellow',
    APPROVED: 'bg-green-lt text-green',
    REJECTED: 'bg-red-lt text-red',
    DISABLED: 'bg-muted-lt text-secondary',
    DEPRECATED: 'bg-orange-lt text-orange'
  };
  return <span className={`badge ${styleByStatus[normalized]}`}>{normalized}</span>;
}

export function resolveTemplateEncoding(template: Pick<TemplateItem, 'encoding' | 'body'>): 'GSM_7' | 'UCS_2' {
  if (template.encoding === 'GSM_7' || template.encoding === 'UCS_2') {
    return template.encoding;
  }
  return getTemplateEncoding(template.body) === 'GSM-7' ? 'GSM_7' : 'UCS_2';
}

function renderTemplateEncodingBadge(template: TemplateItem) {
  const normalizedEncoding = resolveTemplateEncoding(template);
  const label = normalizedEncoding === 'GSM_7' ? 'GSM-7' : 'UCS-2';
  const badgeClass = normalizedEncoding === 'GSM_7' ? 'bg-green-lt text-green' : 'bg-orange-lt text-orange';
  return <span className={`badge ${badgeClass}`}>{label}</span>;
}

const TEMPLATE_KEY_PATTERN = /^[A-Za-z0-9._:-]{1,128}$/;
const TEMPLATE_KEY_FORMAT_MESSAGE =
  'Template key must use 1-128 characters from A-Z, a-z, 0-9, ".", "_", ":", or "-".';

function validateDraft(draft: DraftTemplate): string | null {
  const templateKey = draft.template_key.trim();
  if (!templateKey) {
    return 'Template key is required.';
  }
  if (!TEMPLATE_KEY_PATTERN.test(templateKey)) {
    return TEMPLATE_KEY_FORMAT_MESSAGE;
  }
  if (!draft.body.trim()) {
    return 'Template body is required.';
  }
  return null;
}

function parseTemplateRequiredVars(requiredVarsJson: string | null): string[] {
  if (!requiredVarsJson) {
    return [];
  }
  try {
    const parsed = JSON.parse(requiredVarsJson);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((item): item is string => typeof item === 'string');
  } catch {
    return [];
  }
}

function extractDetectedVariables(body: string): string[] {
  const regex = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
  const ordered: string[] = [];
  const seen = new Set<string>();
  let match = regex.exec(body);
  while (match) {
    const variable = match[1];
    if (!seen.has(variable)) {
      seen.add(variable);
      ordered.push(variable);
    }
    match = regex.exec(body);
  }
  return ordered;
}

function buildSelectionFromDetected(
  detected: string[],
  previousSelection: RequiredVarSelection,
  defaultValue: boolean
): RequiredVarSelection {
  const nextSelection: RequiredVarSelection = {};
  for (const variable of detected) {
    if (Object.prototype.hasOwnProperty.call(previousSelection, variable)) {
      nextSelection[variable] = previousSelection[variable];
    } else {
      nextSelection[variable] = defaultValue;
    }
  }
  return nextSelection;
}

function toRequiredVars(selection: RequiredVarSelection, detected: string[]): string[] | null {
  const required = detected.filter((variable) => selection[variable]);
  if (!required.length) {
    return null;
  }
  return required;
}

type TemplatesRouteProps = {
  accountId: string | null;
  timeDisplayMode: TimeDisplayMode;
};

export function TemplatesRoute({ accountId, timeDisplayMode }: TemplatesRouteProps) {
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplateKey, setEditingTemplateKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftTemplate>({
    template_key: '',
    body: ''
  });
  const [requiredVarSelection, setRequiredVarSelection] = useState<RequiredVarSelection>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteTemplateKey, setDeleteTemplateKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportNotice, setExportNotice] = useState<string | null>(null);
  const [filters, setFilters] = useState<TemplateFilters>({
    templateKey: '',
    status: 'all',
    bodyContains: ''
  });
  const autoOpenedTemplateKeyRef = useRef<string | null>(null);

  const isEditing = editingTemplateKey !== null;
  const modalTitle = isEditing ? 'Edit template' : 'Create template';
  const detectedVariables = useMemo(() => extractDetectedVariables(draft.body), [draft.body]);
  const encodingName = useMemo(() => getTemplateEncoding(draft.body), [draft.body]);
  const characterCount = useMemo(() => Array.from(draft.body).length, [draft.body]);
  const segmentCount = useMemo(() => estimateSmsSegments(draft.body, encodingName), [draft.body, encodingName]);
  const nonGsmMatches = useMemo(() => getNonGsmCharacterMatches(draft.body), [draft.body]);
  const hasNonGsm = nonGsmMatches.length > 0;
  const bodyPreviewChunks = useMemo(() => buildBodyPreviewChunks(draft.body), [draft.body]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadingError(null);
    fetchTemplates(accountId)
      .then((response) => {
        if (cancelled) return;
        setTemplates(response.items);
      })
      .catch((error) => {
        if (cancelled) return;
        setLoadingError(error instanceof Error ? error.message : String(error));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  useEffect(() => {
    if (loading || modalOpen) {
      return;
    }
    const templateKey = new URLSearchParams(window.location.search).get('templateKey')?.trim();
    if (!templateKey) {
      return;
    }
    if (autoOpenedTemplateKeyRef.current === templateKey) {
      return;
    }
    const template = templates.find((item) => item.template_key === templateKey);
    if (!template) {
      return;
    }
    autoOpenedTemplateKeyRef.current = templateKey;
    openEditModal(template);
  }, [loading, modalOpen, templates]);

  async function refreshTemplates(): Promise<void> {
    const response = await fetchTemplates(accountId);
    setTemplates(response.items);
  }

  const sortedTemplates = useMemo(
    () => {
      if (!Array.isArray(templates)) {
        return [];
      }
      return [...templates].sort((a, b) => {
        if (a.updated_at > b.updated_at) return -1;
        if (a.updated_at < b.updated_at) return 1;
        return a.template_key.localeCompare(b.template_key);
      });
    },
    [templates]
  );

  const visibleTemplates = useMemo(() => {
    const templateKey = filters.templateKey.trim().toLowerCase();
    const bodyContains = filters.bodyContains.trim().toLowerCase();
    return sortedTemplates.filter((template) => {
      if (filters.status !== 'all' && template.status !== filters.status) {
        return false;
      }
      if (templateKey.length > 0 && !template.template_key.toLowerCase().includes(templateKey)) {
        return false;
      }
      if (bodyContains.length > 0 && !template.body.toLowerCase().includes(bodyContains)) {
        return false;
      }
      return true;
    });
  }, [filters.bodyContains, filters.status, filters.templateKey, sortedTemplates]);

  const hasActiveFilters =
    filters.status !== 'all' ||
    filters.templateKey.trim().length > 0 ||
    filters.bodyContains.trim().length > 0;

  function openCreateModal() {
    setEditingTemplateKey(null);
    setDraft({
      template_key: '',
      body: ''
    });
    setRequiredVarSelection({});
    setFormError(null);
    setModalOpen(true);
  }

  function openEditModal(template: TemplateItem) {
    setEditingTemplateKey(template.template_key);
    setDraft({
      template_key: template.template_key,
      body: template.body
    });
    const detected = extractDetectedVariables(template.body);
    const parsedRequiredVars = new Set(parseTemplateRequiredVars(template.required_vars_json));
    const nextSelection: RequiredVarSelection = {};
    for (const variable of detected) {
      nextSelection[variable] = parsedRequiredVars.has(variable);
    }
    setRequiredVarSelection(nextSelection);
    setFormError(null);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingTemplateKey(null);
    setRequiredVarSelection({});
    setFormError(null);
  }

  async function handleUpsertTemplate(event: FormEvent) {
    event.preventDefault();
    const validationError = validateDraft(draft);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const nextTemplateKey = draft.template_key.trim();
      const requiredVars = toRequiredVars(requiredVarSelection, detectedVariables);
      await upsertTemplate({
        templateKey: nextTemplateKey,
        body: draft.body.trim(),
        requiredVars
      });

      if (isEditing && editingTemplateKey && editingTemplateKey !== nextTemplateKey) {
        await deleteTemplate(editingTemplateKey);
      }
      await refreshTemplates();
      closeModal();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : String(error));
    } finally {
      setSaving(false);
    }
  }

  function applyGsm7Replacements(): void {
    const replacedBody = applySmartReplacements(draft.body);
    if (replacedBody === draft.body) {
      return;
    }
    const nextDetected = extractDetectedVariables(replacedBody);
    setDraft((current) => ({ ...current, body: replacedBody }));
    setRequiredVarSelection((current) => buildSelectionFromDetected(nextDetected, current, true));
  }

  function confirmDelete(templateKey: string) {
    setDeleteTemplateKey(templateKey);
  }

  function cancelDelete() {
    setDeleteTemplateKey(null);
  }

  async function executeDelete() {
    if (!deleteTemplateKey) return;
    setDeleting(true);
    setLoadingError(null);
    try {
      await deleteTemplate(deleteTemplateKey);
      await refreshTemplates();
      setDeleteTemplateKey(null);
    } catch (error) {
      setLoadingError(error instanceof Error ? error.message : String(error));
    } finally {
      setDeleting(false);
    }
  }

  async function exportTemplatesCsv(): Promise<void> {
    const query = new URLSearchParams({ format: 'csv' });
    if (accountId && accountId.trim().length > 0) {
      query.set('accountId', accountId.trim());
    }
    if (filters.status !== 'all') {
      query.set('status', filters.status);
    }
    if (filters.templateKey.trim().length > 0) {
      query.set('templateKey', filters.templateKey.trim());
    }
    if (filters.bodyContains.trim().length > 0) {
      query.set('bodyContains', filters.bodyContains.trim());
    }
    setExporting(true);
    setLoadingError(null);
    setExportNotice(null);
    try {
      const response = await fetch(`/api/templates?${query.toString()}`, {
        method: 'GET',
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`Template export failed (${response.status})`);
      }
      setExportNotice('Template export started. Track status and download in Reports > Export Jobs.');
    } catch (error) {
      setLoadingError(error instanceof Error ? error.message : String(error));
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <div className="page-pretitle">Messaging</div>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h2 className="page-title mb-0">Templates</h2>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-primary" onClick={() => void exportTemplatesCsv()} disabled={exporting}>
            {exporting ? 'Exporting...' : 'Export current view (CSV)'}
          </button>
          <button type="button" className="btn btn-primary" onClick={openCreateModal}>
            Create template
          </button>
        </div>
      </div>
      {loadingError ? <div className="alert alert-danger">{loadingError}</div> : null}
      {exportNotice ? (
        <div className="alert alert-success d-flex justify-content-between align-items-center">
          <span>{exportNotice}</span>
          <Link className="btn btn-sm btn-outline-success" to="/reports">
            View Export Jobs
          </Link>
        </div>
      ) : null}

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Existing templates</h3>
        </div>
        <div className="card-body border-bottom">
          <div className="row g-2">
            <div className="col-sm-4">
              <label className="form-label mb-1">Template Key</label>
              <input
                className="form-control form-control-sm"
                value={filters.templateKey}
                onChange={(event) => setFilters((current) => ({ ...current, templateKey: event.target.value }))}
                placeholder="template key contains..."
              />
            </div>
            <div className="col-sm-3">
              <label className="form-label mb-1">Status</label>
              <select
                className="form-select form-select-sm"
                value={filters.status}
                onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value as TemplateStatusFilter }))}
              >
                <option value="all">All statuses</option>
                <option value="IN_REVIEW">IN_REVIEW</option>
                <option value="APPROVED">APPROVED</option>
                <option value="REJECTED">REJECTED</option>
                <option value="DISABLED">DISABLED</option>
                <option value="DEPRECATED">DEPRECATED</option>
              </select>
            </div>
            <div className="col-sm-4">
              <label className="form-label mb-1">Body Contains</label>
              <input
                className="form-control form-control-sm"
                value={filters.bodyContains}
                onChange={(event) => setFilters((current) => ({ ...current, bodyContains: event.target.value }))}
                placeholder="text search..."
              />
            </div>
            <div className="col-sm-1 d-flex align-items-end">
              <button
                type="button"
                className="btn btn-sm btn-ghost-secondary w-100"
                onClick={() =>
                  setFilters({
                    templateKey: '',
                    status: 'all',
                    bodyContains: ''
                  })
                }
              >
                Clear
              </button>
            </div>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table card-table table-vcenter">
            <thead>
              <tr>
                <th>Key</th>
                <th>Status</th>
                <th>Encoding</th>
                <th>Required Vars</th>
                <th>Updated</th>
                <th>Body</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleTemplates.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-secondary text-center py-4">
                    {loading
                      ? 'Loading templates...'
                      : hasActiveFilters
                        ? 'No templates matched the current filters.'
                        : 'No templates are available for the current account scope.'}
                  </td>
                </tr>
              ) : (
                visibleTemplates.map((template) => (
                  <tr key={template.id ?? template.template_key}>
                    <td className="fw-semibold">{template.template_key}</td>
                    <td>{renderTemplateStatusBadge(template.status)}</td>
                    <td>{renderTemplateEncodingBadge(template)}</td>
                    <td className="text-secondary template-vars-cell">
                      {template.required_vars_json ? template.required_vars_json : '-'}
                    </td>
                    <td className="text-secondary">
                      {formatPortalDateTime(template.updated_at, timeDisplayMode, {
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      })}
                    </td>
                    <td className="portal-wrap">{template.body}</td>
                    <td className="text-end">
                      <div className="btn-list justify-content-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => openEditModal(template)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => confirmDelete(template.template_key)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen ? (
        <div
          className="modal modal-blur fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={handleUpsertTemplate}>
                <div className="modal-header">
                  <h5 className="modal-title">{modalTitle}</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={closeModal} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Template key</label>
                    <input
                      className="form-control"
                      value={draft.template_key}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, template_key: event.target.value }))
                      }
                      placeholder="example_template_key"
                      disabled={saving}
                    />
                    <div className="form-hint mt-2">{TEMPLATE_KEY_FORMAT_MESSAGE}</div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Body</label>
                    <textarea
                      className="form-control"
                      rows={4}
                      value={draft.body}
                      onChange={(event) => {
                        const nextBody = event.target.value;
                        const nextDetected = extractDetectedVariables(nextBody);
                        setDraft((current) => ({ ...current, body: nextBody }));
                        setRequiredVarSelection((current) => buildSelectionFromDetected(nextDetected, current, true));
                      }}
                      placeholder="Hi {{first_name}}, this is your message..."
                      disabled={saving}
                    />
                    <div className="template-encoding-row mt-2">
                      <span className="text-secondary small">Encoding</span>
                      <span className={`badge ${hasNonGsm ? 'bg-red-lt text-red' : 'bg-green-lt text-green'}`}>{encodingName}</span>
                      <span className="text-secondary small">SMS Segments</span>
                      <span className="badge bg-azure-lt text-azure">{segmentCount}</span>
                      <span className="text-secondary small">Characters</span>
                      <span className="badge bg-blue-lt text-blue">{characterCount}</span>
                    </div>
                  </div>

                  {detectedVariables.length > 0 ? (
                    <div className="mb-3">
                      <div className="alert alert-warning mb-0">
                        Variable placeholders are present in this template. Final message length and SMS segment count depend on
                        the lengths of supplied variable values at send time.
                      </div>
                    </div>
                  ) : null}

                  <div className="mb-3">
                    {hasNonGsm ? (
                      <div className="alert alert-danger mb-0">
                        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                          <span>
                            This template contains non-GSM7 characters and may result in multiple segments at additional cost.
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={applyGsm7Replacements}
                            disabled={saving}
                          >
                            Replace With GSM-7 Equivalents
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="alert alert-success mb-0">
                        No non-GSM-7 characters detected.
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Live preview (non-GSM highlighted)</label>
                    <div className="template-body-preview" aria-live="polite">
                      {bodyPreviewChunks.length === 0 ? (
                        <span className="text-secondary">Template body preview will appear here.</span>
                      ) : (
                        bodyPreviewChunks.map((chunk, index) =>
                          chunk.isNonGsm ? (
                            <span key={`${index}-ng`} className="template-body-preview-highlight">
                              {chunk.text}
                            </span>
                          ) : (
                            <span key={`${index}-ok`}>{chunk.text}</span>
                          )
                        )
                      )}
                    </div>
                  </div>

                  {hasNonGsm ? (
                    <div className="mb-3">
                      <label className="form-label">Detected non-GSM-7 characters</label>
                      <div className="table-responsive">
                        <table className="table table-sm table-vcenter mb-0">
                          <thead>
                            <tr>
                              <th>Character</th>
                              <th>Code point</th>
                              <th>Index</th>
                              <th>Suggested replacement</th>
                            </tr>
                          </thead>
                          <tbody>
                            {nonGsmMatches.map((match) => (
                              <tr key={`${match.index}-${match.codePoint}`}>
                                <td>
                                  <code>{match.character}</code>
                                </td>
                                <td>{match.codePoint}</td>
                                <td>{match.index}</td>
                                <td>{match.suggested ?? 'No automatic replacement'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : null}

                  <div className="mb-0">
                    <label className="form-label">Detected variables</label>
                    {detectedVariables.length === 0 ? (
                      <div className="form-hint">No template variables detected in body. Use syntax like {'{{first_name}}'}.</div>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {detectedVariables.map((variable) => (
                          <label key={variable} className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={Boolean(requiredVarSelection[variable])}
                              onChange={(event) =>
                                setRequiredVarSelection((current) => ({
                                  ...current,
                                  [variable]: event.target.checked
                                }))
                              }
                              disabled={saving}
                            />
                            <span className="form-check-label">
                              <code>{variable}</code> required
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                    <div className="form-hint mt-2">
                      Required vars are detected from the template body and selected here to reduce manual entry errors.
                    </div>
                  </div>
                  {formError ? <div className="alert alert-danger mt-3 mb-0">{formError}</div> : null}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-link link-secondary" onClick={closeModal} disabled={saving}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary ms-auto" disabled={saving}>
                    {isEditing ? 'Save changes' : 'Create template'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      {deleteTemplateKey ? (
        <div
          className="modal modal-blur fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete template</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={cancelDelete} />
              </div>
              <div className="modal-body">
                Are you sure you want to delete this template? This action removes it from the configured account.
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={cancelDelete} disabled={deleting}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger ms-auto" onClick={executeDelete} disabled={deleting}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
